import { useRef, useCallback } from 'react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { isCapacitor } from '../../utils/platform';
import { Capacitor } from '@capacitor/core';
import { createLogger } from '../../services/Debug';
export function useRecordingControls({
  isRecording,
  setIsRecording,
  startRecording,
  stopRecording,
  pauseRecording,   // Real pause for airplane mode
  resumeRecording,  // Resume after pause
  addToQueue,
  processQueue,
  processingRef,
  queuePaused,
  manualAirplaneMode,
  setQueuePaused,
  showNotification,
  setIsSendDisable,
  setisFakeRecording,
  onStopRecordingComplete,
  onSegmentCaptured,
  recordingDuration = 0, // Duration from recorder hook (ms)
  nativeDurationRef = null, // Ref for native duration (to get latest value)
  isNative = false, // Platform flag
  isPaused = false, // Track if recording is paused (from parent)
  setIsPaused = () => {} // Setter for pause state (from parent)
}) {
  const recordingStartTimeRef = useRef(null);
  const log = createLogger('useRecordingControls');

  const stopRecordingCompletely = useCallback(async () => {
    if (isRecording) {
      const finalAudio = await stopRecording();  // Use platform-specific stop
      setIsRecording(false);

      // Update UI to show completely stopped state
      setIsSendDisable(false);

      // Add final audio to queue if there's any content
      if (finalAudio && finalAudio.size > 0) {
        const audioId = Date.now().toString();
        // Convert duration from ms to seconds
        // For native, read from ref to get the latest value set during stopRecording()
        const durationMs = isNative && nativeDurationRef ? nativeDurationRef.current : recordingDuration;
        const durationSeconds = durationMs > 0 ? Math.round(durationMs / 1000) : undefined;

        // Allow caller to handle final audio (e.g., live mode behavior)
        if (typeof onStopRecordingComplete === 'function') {
          try {
            const handled = await onStopRecordingComplete(finalAudio, { durationSeconds });
            if (handled) {
              return;
            }
          } catch (err) {
            log.error('onStopRecordingComplete error:', err);
          }
        }

        addToQueue(finalAudio, audioId, durationSeconds ? { durationSeconds } : {});

        setTimeout(() => {
          if (!queuePaused && !processingRef.current) {
            processQueue();
          }
        }, 100);
      }
    }

    // Reset pause state after stopping - must be outside the if block
    // to handle airplane mode where isRecording is false when paused
    setIsPaused(false);
  }, [
    isRecording, stopRecording, setIsRecording, setisFakeRecording,
    setIsSendDisable, addToQueue,
    queuePaused, processingRef, processQueue, onStopRecordingComplete, recordingDuration,
    isNative, nativeDurationRef
  ]);

  const triggerRecord = useCallback(async () => {
    if (isCapacitor) {
      Haptics.impact({
        style: ImpactStyle.Light
      });
    }

    if (isRecording) {
      // Check if in airplane mode - use real pause instead of fake pause
      if (manualAirplaneMode && pauseRecording) {
        try {
      

          await pauseRecording(); // This updates isRecording internally
          setIsPaused(true);
          return;
        } catch (error) {
          log.error('❌ Error pausing recording:', error);
          return;
        }
      }

      // RED button behavior: "Fake pause" - get audio segment and transcribe


      try {
        // Check minimum recording duration (milliseconds since start)
        const elapsedMs = recordingStartTimeRef.current 
          ? Date.now() - recordingStartTimeRef.current 
          : 0;
        
        if (elapsedMs < 500) {
          log.warn('⚠️ Recording too short, ignoring stop request');
          showNotification('Enregistrement trop court, continuez à parler', 'warning');
          return;
        }
        
        // Stop and get the audio segment
       // Stop and get the audio segment
       const audioSegment = await stopRecording();  // Use platform-specific stop
       
       
       // Check if we got valid audio data
       if (!audioSegment || audioSegment.size === 0) {
         log.warn('⚠️ No audio data captured, ignoring');


         // Reset UI states but don't add to queue
         setIsRecording(false);
         setIsPaused(false); // Reset pause state


         recordingStartTimeRef.current = null;
         return;
       }
        
        // Reset start time since we're stopping
        recordingStartTimeRef.current = null;

        // Update UI states
        setIsRecording(false);
        setIsSendDisable(false);
        setIsPaused(false); // Reset pause state after stopping

        // Add the audio segment to transcription queue
        const audioId = Date.now().toString();

        // Convert duration from ms to seconds
        // For native, read from ref to get the latest value set during stopRecording()
        const durationMs = isNative && nativeDurationRef ? nativeDurationRef.current : recordingDuration;
        const durationSeconds = durationMs > 0 ? Math.round(durationMs / 1000) : undefined;

        // Allow caller to handle segment directly (e.g., live mode paste or send)
        if (typeof onSegmentCaptured === 'function') {
          try {
            const handled = await onSegmentCaptured(audioSegment, { durationSeconds });
            if (handled) {
              return;
            }
          } catch (err) {
            log.error('onSegmentCaptured error:', err);
          }
        }

        addToQueue(audioSegment, audioId, durationSeconds ? { durationSeconds } : {});
        
        // Resume queue if paused (but not if manually in airplane mode)
        if (queuePaused && !manualAirplaneMode) {
          setQueuePaused(false);
        }
        
        // Start transcription with a small delay
        setTimeout(() => {
            
          if (!processingRef.current) {
            processQueue();
          }
        }, 100);
        
      } catch (error) {
        log.error('❌ Error during fake pause process:', error);
      }
    } else {
      // Check if resuming from pause (airplane mode)
      if (isPaused && resumeRecording) {
        try {
          await resumeRecording(); // This updates isRecording internally
          setIsPaused(false);
          return;
        } catch (error) {
          log.error('❌ Error resuming recording:', error);
          return;
        }
      }

      // Starting recording (either fresh start or resume from fake pause)
      const success = await startRecording();  // Use platform-specific start
      if (!success) {
        if (!isCapacitor) {
          alert('Aucun microphone détecté. Veuillez connecter un microphone et réessayer.');
        }
        return;
      }

      // Record start time
      recordingStartTimeRef.current = Date.now();

      // Update UI to show recording state
      setIsSendDisable(true);
      setisFakeRecording(true);
      setIsRecording(true); // Set recording state to true
    }
  }, [
    isCapacitor, isRecording, isPaused,
    stopRecording, pauseRecording, resumeRecording, addToQueue,
    queuePaused, manualAirplaneMode, setQueuePaused, showNotification, processingRef, processQueue,
    startRecording, setisFakeRecording, setIsSendDisable, stopRecordingCompletely, onSegmentCaptured, recordingDuration,
    isNative, nativeDurationRef
  ]);

  const handleTranscribe = useCallback(async () => {
    const finalAudio = await stopRecording();  // Use platform-specific stop

    const audioId = Date.now().toString();
    // For native, read from ref to get the latest value set during stopRecording()
    const durationMs = isNative && nativeDurationRef ? nativeDurationRef.current : recordingDuration;
    const durationSeconds = durationMs > 0 ? Math.round(durationMs / 1000) : undefined;
    addToQueue(finalAudio, audioId, durationSeconds ? { durationSeconds } : {});

    if (queuePaused && !manualAirplaneMode) {
      setQueuePaused(false);
    }

    setTimeout(() => {
      if (!processingRef.current) {
        processQueue();
      }
    }, 100);

    setIsSendDisable(false);
    setIsPaused(false); // Reset pause state after transcription
  }, [stopRecording, addToQueue, queuePaused, manualAirplaneMode, setQueuePaused, showNotification, processingRef, processQueue, setIsSendDisable, recordingDuration, isNative, nativeDurationRef]);



  return {
    triggerRecord,
    handleTranscribe,
    stopRecordingCompletely
  };
}